import * as React from 'react'
import { PropsWithChildren } from 'react'
import { cn } from '@/lib/utils'
import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'

const badgeVariants = cva(
  'group/badge inline-flex h-6 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-4xl border border-transparent px-3 py-0.5 text-sm font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!',
  {
    variants: {
      variant: {
        status:
          'bg-primary h-7 text-xs font-semibold uppercase text-primary-foreground [a]:hover:bg-primary/80',
        default: 'bg-primary text-primary-foreground [a]:hover:bg-primary/80',
        secondary: 'bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80',
        company: 'bg-[rgba(0,0,0,0.05)] text-xs px-2 text-[#222222] border-transparent rounded-md',
        destructive:
          'bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20',
        outline: 'border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground',
        ghost: 'hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50',
        link: 'text-primary underline-offset-4 hover:underline',
        neutral: 'border-border bg-muted text-muted-foreground',
        info: 'border-transparent bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
        green:
          'border-transparent bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
        red: 'border-transparent bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
        orange:
          'border-transparent bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
        light: 'border-border/60 bg-background text-foreground/80',
        'outline-primary': 'bg-transparent text-[#922c88] border-[#922c88] rounded-full',
        'outline-info':
          'border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-400/25 dark:bg-sky-500/10 dark:text-sky-300',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function Badge({
  className,
  variant = 'default',
  render,
  ...props
}: useRender.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ variant }), className, props.onClick && 'cursor-pointer'),
      },
      props
    ),
    render,
    state: {
      slot: 'badge',
      variant,
    },
  })
}

const CompanyTag: React.FC<
  PropsWithChildren<{
    id: number | string
  }>
> = ({ id, children, ...badgeProps }) => {
  const styles = getIndividualStyles(id)
  const companyName = getCompanyName(id)

  return (
    <Badge
      variant='company'
      style={{ background: styles.background, color: styles.color }}
      {...badgeProps}
    >
      {companyName || children || styles.text}
    </Badge>
  )
}

const getIndividualStyles = (id: number | string) => {
  switch (id) {
    case 0:
      return { background: 'rgba(0, 0, 0, 0.05)', color: '#222222' }
    case 1:
    case '1':
      return { text: 'TMEU', background: '#FF9393', color: '#222222' }
    case 2:
    case '2':
      return { text: 'TMUK', background: '#EBFFD3', color: '#222222' }
    case 3:
    case '3':
      return { text: 'TMSC', background: '#A8C9FF', color: '#222222' }
    case 4:
    case '4':
      return { text: 'TMPA', background: '#FFA354', color: '#222222' }
    case 5:
    case '5':
      return { text: 'TMAS', background: '#52c41a', color: '#222222' }
    case 'IB':
      return { background: '#ffd7b5', color: '#222222' }
    case 'ClientPaymentAgent':
      return { background: '#f7dc39', color: '#222222' }
    case 'AdditionalDeposit':
      return { background: '#d2CCFF', color: '#222222' }
    case 'WithdrawAmountSplit':
      return { background: '#f2c41a', color: '#222222' }
    default:
      return { background: '#52c41a', color: '#222222' }
  }
}

const getCompanyName = (id: number | string): string | undefined => {
  switch (id) {
    case 1:
    case '1':
      return 'TMEU'
    case 2:
    case '2':
      return 'TMUK'
    case 3:
    case '3':
      return 'TMSC'
    case 4:
    case '4':
      return 'TMPA'
    case 5:
    case '5':
      return 'TMAS'
    default:
      return undefined
  }
}

export { Badge, CompanyTag, badgeVariants }
